<template>
    <Modal
        id="create-formula-modal"
        ref="createFormulaModal"
        v-model:open="open"
        :title="$t('create_formula')"
        @cancel="cancel"
        @submit="submit"
    >
        <div
            class="flex h-auto w-full flex-col items-start justify-center space-y-2"
        >
            <p class="text-sm text-gray-500">
                {{ $t('formula') }}
            </p>
            <textarea
                v-model="formula"
                class="focus:border-primary-accent-500 h-40 w-full rounded-md border border-gray-300 bg-slate-100 p-2 focus:outline-none focus:ring-0"
            ></textarea>
        </div>
        <div class="mt-6 grid h-auto w-full grid-cols-2 gap-2">
            <div
                class="col-span-1 grid h-44 grid-cols-1 content-start gap-2 overflow-y-auto rounded-md border border-gray-300 bg-slate-50 p-2"
            >
                <button
                    v-for="tax in taxPlaceholders"
                    :key="tax"
                    class="col-span-1 flex h-10 cursor-pointer items-center justify-center rounded-md bg-slate-200 text-sm text-black"
                    @click="selectedParts.push(`${tax}`)"
                >
                    {{ tax }}
                </button>
            </div>
            <div
                class="col-span-1 flex flex-col items-start justify-start space-y-2"
            >
                <div
                    class="grid h-32 w-full grid-cols-4 gap-2 rounded-md border border-gray-300 bg-slate-50 p-2"
                >
                    <div
                        v-for="operator in operators"
                        :key="operator"
                        class="col-span-1 flex cursor-pointer items-center justify-center rounded-md bg-slate-200 text-sm text-black"
                        @click="selectedParts.push(operator)"
                    >
                        {{ operator }}
                    </div>
                </div>
                <div
                    class="relative h-10 w-full rounded-md border border-gray-300"
                >
                    <input
                        v-model="number"
                        type="number"
                        placeholder="Enter a number"
                        class="h-full w-full rounded-md border-none outline-none focus:ring-0"
                    />
                    <button
                        class="bg-primary-accent-500 absolute right-1 top-1/2 h-4/5 w-auto -translate-y-1/2 rounded-md px-2 text-sm text-white"
                        @click="addNumberToSelectedParts"
                    >
                        Add
                    </button>
                </div>
            </div>
        </div>

        <!-- Validation Error Display -->
        <div
            v-if="!validationResult.isValid && validationResult.errorMessage"
            class="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700"
        >
            <p class="font-medium">Formula Error:</p>
            <p>{{ validationResult.errorMessage }}</p>
        </div>

        <button
            :class="[
                'mt-6 h-10 w-full rounded-md px-2 text-sm text-white',
                createDisabled
                    ? 'cursor-not-allowed bg-gray-400'
                    : 'bg-primary-accent-500',
            ]"
            :disabled="createDisabled || !formula"
            @click="submit"
        >
            Create
        </button>
    </Modal>
</template>

<script setup lang="ts">
import Modal from '@src/components/Modal.vue'
import {
    parseFormula,
    validateTaxFormulaExpression,
    type TaxFormulaValidationResult,
} from '@src/composables/taxes/taxes'
import { getTaxNamePlaceholders } from '@src/utils/api/taxes'
import { useVModels } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'

const props = defineProps<{
    open: boolean
    type: 'amount' | 'rate' | 'tax_detail_rate' | 'tax_detail_amount'
    name?: string
    formula: string
    taxDetails: string[]
}>()

const emit = defineEmits(['cancel', 'submit'])

const { open } = useVModels(props, emit)

const selectedParts = ref<string[]>([])
const number = ref(0)
const formula = ref()
const taxPlaceholders = ref<string[]>([])
const validationResult = ref<TaxFormulaValidationResult>({ isValid: true })
const createDisabled = ref(true)

const cancel = () => {
    emit('cancel')
}

const submit = () => {
    emit('submit', formula.value)
    open.value = false
}

const operators = ['+', '-', '/', '*', '(', ')']

const addNumberToSelectedParts = () => {
    selectedParts.value.push(number.value.toString())
    number.value = 0
}

watch(
    () => selectedParts.value,
    () => {
        formula.value = selectedParts.value.join('')
    },
    {
        deep: true,
    }
)

watch(
    () => formula.value,
    () => {
        setTimeout(() => {
            createDisabled.value = true
            selectedParts.value = parseFormula(
                formula.value,
                taxPlaceholders.value
            )
            validationResult.value = validateTaxFormulaExpression(
                formula.value,
                taxPlaceholders.value
            )
            createDisabled.value = !validationResult.value.isValid
        }, 1000)
    },
    {
        deep: true,
    }
)

onMounted(async () => {
    formula.value = props.formula
    const taxNamePlaceholdersRequest = await getTaxNamePlaceholders()
    taxPlaceholders.value.push(
        ...taxNamePlaceholdersRequest.data.name_placeholders
    )
    props.taxDetails.forEach((detail) => {
        if (
            !taxPlaceholders.value.find(
                (placeholder) =>
                    placeholder === `{{${detail}_rate}}` ||
                    placeholder === `{{${detail}_amount}}`
            )
        ) {
            taxPlaceholders.value.push(`{{${detail}_rate}}`)
            taxPlaceholders.value.push(`{{${detail}_amount}}`)
        }
    })
})
</script>
